import axios from 'axios';
import { apiRoute } from '../constants/api-constants';
import {
  IqEntitlement,
  ShiftSummaryResponse,
  AskResponse,
  IqUsage,
} from '../interfaces/iq-interfaces';

// 75s, DERIVED: server worst case is the semaphore wait (AI_MAX_CONCURRENT x
// AI_TIMEOUT_MS x 2 attempts) + the Gate-0 wait + two short DB round trips + the
// coalescing poll + slack. The client must not be the first thing to give up —
// an aborted Gemini call is still billed.
const IQ_TIMEOUT = 75000;

export class IqDisabledError extends Error {
  readonly reason: string;
  readonly canBuy: boolean;
  constructor(message: string, reason: string, canBuy: boolean) {
    super(message);
    this.name = 'IqDisabledError';
    this.reason = reason;
    this.canBuy = canBuy;
  }
}

export class IqBudgetError extends Error {
  readonly spentMicros: number;
  readonly budgetMicros: number;
  readonly canBuy: boolean;
  constructor(m: string, spent: number, budget: number, canBuy: boolean) {
    super(m);
    this.name = 'IqBudgetError';
    this.spentMicros = spent;
    this.budgetMicros = budget;
    this.canBuy = canBuy;
  }
}

interface IqErrorBody {
  message?: string;
  code?: string;
  reason?: string;
  can_buy?: boolean;
  spent_micros?: number;
  budget_micros?: number;
}

export const asIqError = (error: unknown): unknown => {
  if (!axios.isAxiosError(error)) return error;
  const status = error.response?.status;
  const data = error.response?.data as IqErrorBody | undefined;

  if ((status === 402 || status === 403) && data?.code === 'IQ_NOT_ENABLED') {
    return new IqDisabledError(
      data.message || 'Lobbero IQ is not enabled for this account.',
      data.reason || 'unknown',
      data.can_buy === true
    );
  }
  if (status === 429 && data?.code === 'IQ_BUDGET_EXCEEDED') {
    return new IqBudgetError(
      data.message || 'Lobbero IQ has reached this month’s limit.',
      data.spent_micros ?? 0,
      data.budget_micros ?? 0,
      data.can_buy === true
    );
  }

  return error;
};

export const getIqEntitlement = (): Promise<IqEntitlement> =>
  axios.get<IqEntitlement>(`${apiRoute}/iq/entitlement`).then(r => r.data);

// THE CACHE READ. Carries rlsContext, never calls Gemini. Cheap and safe.
export const getHandover = (params: {
  building_id: number;
  from?: string;
  to?: string;
}): Promise<ShiftSummaryResponse> =>
  axios
    .get<ShiftSummaryResponse>(`${apiRoute}/iq/shift-summary`, {
      params,
      timeout: 15000,
    })
    .then(r => r.data)
    .catch(e => {
      throw asIqError(e);
    });

export const generateHandover = (
  body: {
    building_id: number;
    from?: string;
    to?: string;
    force?: boolean;
  },
  opts?: { signal?: AbortSignal }
): Promise<ShiftSummaryResponse> =>
  axios
    .post<ShiftSummaryResponse>(`${apiRoute}/iq/shift-summary`, body, {
      timeout: IQ_TIMEOUT,
      signal: opts?.signal,
    })
    .then(r => r.data)
    .catch(e => {
      throw asIqError(e);
    });

// building_id IS OPTIONAL ON THE WIRE (WP-22, lobbero_be/src/CRUD/iq.js:680-687):
// absent means "everything this person can see", which RLS decides — an
// organisation admin gets the whole organisation, a concierge gets their
// assigned buildings, and there is no JS branch anywhere that makes that choice.
// The type says so because the contract says so.
//
// BOTH CURRENT CALL SITES STILL SEND IT, DELIBERATELY. IqChat and IqAskPanel are
// building-scoped surfaces — the chat resets its whole thread when the selected
// building changes — so an org-wide answer appearing under a building header
// would be mislabelled by its own container. Exposing the org-wide question
// needs a scope control next to the composer, which is not in WP-16. The
// `org_wide` flag now round-trips to the renderer (see IqAnswer), so the day
// that control lands, the answer can already say which scope it got.
export const askLobbero = (
  body: { building_id?: number; question: string },
  opts?: { signal?: AbortSignal }
): Promise<AskResponse> =>
  axios
    .post<AskResponse>(`${apiRoute}/iq/ask`, body, {
      timeout: IQ_TIMEOUT,
      signal: opts?.signal,
    })
    .then(r => r.data)
    .catch(e => {
      throw asIqError(e);
    });

export const getIqUsage = (): Promise<IqUsage> =>
  axios.get<IqUsage>(`${apiRoute}/iq/usage`).then(r => r.data);

// WP-10 — free-text cleanup. A DRAFT suggestion for one log field.
//
// It NEVER throws for the user's benefit: the desk must not be blocked by a
// tidy-up hint, so every refusal the server can make arrives as a 200 with
// suggestion:null. The only rejection is a 400 for text over the cap, and the
// caller already knows the cap. A network failure resolves to "no suggestion"
// for the same reason.
export interface CleanupResponse {
  original: string;
  suggestion: string | null;
  suggested_kind: string;
  reason: string;
}

export const cleanupText = (
  body: { building_id?: number; text: string; field: string },
  opts?: { signal?: AbortSignal }
): Promise<CleanupResponse> =>
  axios
    .post<CleanupResponse>(`${apiRoute}/iq/cleanup`, body, {
      timeout: IQ_TIMEOUT,
      signal: opts?.signal,
    })
    .then(r => r.data)
    .catch(() => ({
      original: body.text,
      suggestion: null,
      suggested_kind: '',
      reason: 'unavailable',
    }));
